import { FLOPFLIP_STATE_SLICE } from '@flopflip/react-redux';
import adapter from '../flopflipAdapter';
import featureFlags from '../featureFlags';
import defaultFeatureFlags from '../defaultFeatureFlags';
import logger from '../../utils/logger';

// Track memory namespace population state
let isMemoryNamespacePopulated = false;
let memoryFlagsCache = {};
let previousMemoryFlags = {};
let storeSubscribed = false;
let store = null;
let personalInfoEmailFirstSplitValue;
const PERSONAL_INFO_EMAIL_FIRST_STORAGE_KEY = 'personalInfoEmailFirstEnabled';
// Subscribe to Redux store changes to monitor memory namespace
const checkMemoryNamespace = () => {
  if (!store) return;

  const state = store.getState();
  const currentMemoryFlags = state[FLOPFLIP_STATE_SLICE]?.flags?.memory || {};

  // Check if memory namespace just got populated
  const wasEmpty = Object.keys(previousMemoryFlags).length === 0;
  const isNowPopulated = Object.keys(currentMemoryFlags).length > 0;

  if (wasEmpty && isNowPopulated) {
    logger.info('[FlopFlip Memory] Memory namespace now populated with flags:', Object.keys(currentMemoryFlags));
    isMemoryNamespacePopulated = true;
  }

  // Update cache if flags changed
  if (JSON.stringify(currentMemoryFlags) !== JSON.stringify(previousMemoryFlags)) {
    logger.info('[FlopFlip Memory] Flags updated:', currentMemoryFlags);
    memoryFlagsCache = { ...currentMemoryFlags };
  }

  previousMemoryFlags = currentMemoryFlags;
};

// Lazy import to avoid circular dependency
const getStore = () => {
  if (!store) {
    store = require('../configureStore').default;

    // Subscribe to store changes on first access
    if (!storeSubscribed) {
      storeSubscribed = true;
      store.subscribe(checkMemoryNamespace);
    }
  }
  return store;
};

const safeGetFlopflipState = state => {
  const flopflipState = state[FLOPFLIP_STATE_SLICE];
  // Check if adapterIdentifiers exists and is an array
  if (!flopflipState || !Array.isArray(flopflipState.adapterIdentifiers)) {
    return { flags: { ...defaultFeatureFlags } };
  }

  return flopflipState;
};

// Local preview only: take precedence over flags saved when the application was created.
// Do not write this override back to the application or the Preferences service.
export const getLocalStackableSavingsOverride = () => {
  if (process.env.NODE_ENV !== 'development') return null;
  const value = window.__ENV?.REACT_APP_STACKABLE_SAVINGS_REDESIGN_OVERRIDE;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return null;
};

export const getApplicationStackableSavingsFlag = application => {
  try {
    const saved = application?.marketingContextAtCreation
      ?.find(context => context.key === 'featureFlags')?.value;
    const flags = typeof saved === 'string' ? JSON.parse(saved) : saved;
    const value = flags?.STACKABLE_SAVINGS_REDESIGN_ENABLED;
    return value === true || value === 'true';
  } catch {
    return false;
  }
};

export const getFeatureFlag = name => {
  if (name === featureFlags.STACKABLE_SAVINGS_REDESIGN_ENABLED) {
    const override = getLocalStackableSavingsOverride();
    if (override !== null) return override;
  }
  try {
    const state = getStore().getState();
    // This cohort belongs to the active application, not a previous adapter session
    // or an environment default. Read it synchronously before hydration effects run.
    if (name === featureFlags.STACKABLE_SAVINGS_REDESIGN_ENABLED) {
      return getApplicationStackableSavingsFlag(state.application?.application);
    }
    const flopflipState = safeGetFlopflipState(state);

    // In FlopFlip v15 with memory adapter:
    // - Updated flags from adapter.updateFlags() are stored in flags.memory[name]
    // - Initial/default flags are in flags[name]
    // Priority: memory namespace > root flags > defaultFeatureFlags
    const memoryValue = flopflipState?.flags?.memory?.[name];
    const rootValue = flopflipState?.flags?.[name];
    const defaultValue = defaultFeatureFlags[name];
    // Use cached value if available and memory namespace is populated
    const cachedValue = isMemoryNamespacePopulated ? memoryFlagsCache[name] : undefined;
    const finalValue = memoryValue ?? cachedValue ?? rootValue ?? defaultValue ?? false;
    return finalValue;
  } catch (error) {
    logger.warn(`Error accessing feature flag "${name}":`, error);
    return name === featureFlags.STACKABLE_SAVINGS_REDESIGN_ENABLED
      ? false : defaultFeatureFlags[name] ?? false;
  }
};

export const getSplitPercentage = (variable, { strict = false } = {}) => {
  if (strict) {
    const raw = window.__ENV?.[variable];
    if (typeof raw !== 'string' || !/^(?:\d+(?:\.\d*)?|\.\d+)$/.test(raw.trim())) return 0;
    const value = Number(raw);
    return Number.isFinite(value) && value >= 0 && value <= 1 ? value : 0;
  }
  // Retrieve the split percentage from a environment variable.
  // Will default to 0 if missing or invalid, this will in effect disable
  // the feature flag.
  // eslint-disable-next-line no-underscore-dangle
  const value = Number.parseFloat(window.__ENV?.[variable]);
  return Number.isNaN(value) ? 0 : value;
};

export const getSplitFlagValue = percent => {
  // Calculate the split and return true or false
  // Percent should be a number between 0 and 1
  if (typeof percent === 'number') {
    return Math.random() < percent;
  }
  throw new Error(`Invalid percentage: ${percent}`);
};

export const getPersonalInfoEmailFirstSplitValue = () => {
  if (getSplitPercentage('REACT_APP_ACCOUNT_CREATION_EMAIL_FIRST_SPLIT') <= 0) return undefined;
  try {
    const storedValue = localStorage.getItem(PERSONAL_INFO_EMAIL_FIRST_STORAGE_KEY);
    if (storedValue === 'true' || storedValue === 'false') {
      personalInfoEmailFirstSplitValue = storedValue;
      return storedValue;
    }
  } catch { /* Fall back to the in-memory assignment when storage is unavailable. */ }

  if (personalInfoEmailFirstSplitValue !== undefined) {
    try {
      localStorage.setItem(PERSONAL_INFO_EMAIL_FIRST_STORAGE_KEY, personalInfoEmailFirstSplitValue);
    } catch { /* The in-memory assignment remains authoritative for this page session. */ }
    return personalInfoEmailFirstSplitValue;
  }

  personalInfoEmailFirstSplitValue = String(
    getSplitFlagValue(getSplitPercentage('REACT_APP_ACCOUNT_CREATION_EMAIL_FIRST_SPLIT')),
  );
  try {
    localStorage.setItem(PERSONAL_INFO_EMAIL_FIRST_STORAGE_KEY, personalInfoEmailFirstSplitValue);
  } catch { /* The in-memory assignment remains authoritative for this page session. */ }
  return personalInfoEmailFirstSplitValue;
};

export const getFeatureFlags = () => {
  const flags = {};

  const documentationAbTest2Split = getSplitPercentage('REACT_APP_DOCUMENTATION_2_SPLIT');
  // Avoiding setting the value to false if we are not actually running a split test yet
  if (documentationAbTest2Split !== 0) {
    if (getSplitFlagValue(documentationAbTest2Split)) {
      flags.DOCUMENTATION_2_ENABLED = 'true';
    } else {
      flags.DOCUMENTATION_2_ENABLED = 'false';
    }
  }

  const newNavigationSplit = getSplitPercentage('REACT_APP_NEW_NAVIGATION_SPLIT');
  // Avoiding setting the value to false if we are not actually running a split test yet
  if (newNavigationSplit !== 0) {
    if (defaultFeatureFlags[featureFlags.ENABLE_NAVIGATION_SWITCH]
      && getSplitFlagValue(newNavigationSplit)) {
      flags.ENABLE_NEW_NAVIGATION = 'true';
    } else {
      flags.ENABLE_NEW_NAVIGATION = 'false';
    }
  }

  const savingsSplit = window.__ENV?.REACT_APP_STACKABLE_SAVINGS_REDESIGN_SPLIT;
  const split = typeof savingsSplit === 'string' && savingsSplit.trim() !== ''
    ? Number(savingsSplit) : 0;
  // Persist a control assignment too. Returning applicants never redraw or inherit
  // a cohort merely because an environment default changes later.
  flags.STACKABLE_SAVINGS_REDESIGN_ENABLED = String(
    Number.isFinite(split) && split > 0 && split <= 1 && getSplitFlagValue(split),
  );

  const programPageRedesignSplit = getSplitPercentage('REACT_APP_PROGRAM_PAGE_REDESIGN_SPLIT');
  // Store the assigned treatment with the application so FlopFlip restores it on later sessions.
  if (programPageRedesignSplit !== 0) {
    flags.PROGRAM_PAGE_REDESIGN_TREATMENT_ENABLED = getSplitFlagValue(programPageRedesignSplit)
      ? 'true'
      : 'false';
  }

  const dashBoardSplit = getSplitPercentage('REACT_APP_NAE_DASHBOARD_SPLIT');
  if (getSplitFlagValue(dashBoardSplit)) {
    flags.NAE_DASHBOARD_ENABLED = 'true';
  } else {
    flags.NAE_DASHBOARD_ENABLED = 'false';
  }

  const dashBoardFirstSplit = getSplitPercentage('REACT_APP_NAE_DASHBOARD_FIRST_SPLIT');
  if (getSplitFlagValue(dashBoardFirstSplit)) {
    flags.NAE_DASHBOARD_FIRST = 'true';
  } else {
    flags.NAE_DASHBOARD_FIRST = 'false';
  }

  const personalInfoEmailFirstSplit = getSplitPercentage('REACT_APP_ACCOUNT_CREATION_EMAIL_FIRST_SPLIT');
  if (personalInfoEmailFirstSplit > 0) {
    flags.PERSONAL_INFO_EMAIL_FIRST_ENABLED = getPersonalInfoEmailFirstSplitValue();
  }

  const metSplit = getSplitPercentage('REACT_APP_MET_WORK_REQ_AB_SPLIT', { strict: true });
  if (defaultFeatureFlags[featureFlags.MET_WORK_REQ_ENABLED] && metSplit > 0) {
    flags.MET_WORK_REQ_TREATMENT_ENABLED = String(getSplitFlagValue(metSplit));
  }

  return flags;
};

// Presence, not truthiness, determines precedence. Malformed new metadata must
// never resurrect a legacy treatment or the optional QA default.
export const getMetWorkRequirementsAssignment = application => {
  try {
    const saved = application?.marketingContextAtCreation
      ?.find(context => context.key === 'featureFlags')?.value;
    const flags = typeof saved === 'string' ? JSON.parse(saved) : saved;
    if (!flags || typeof flags !== 'object' || Array.isArray(flags)) return undefined;
    const key = ['MET_WORK_REQ_TREATMENT_ENABLED', 'MET_WORK_REQ_ENABLED']
      .find(name => Object.prototype.hasOwnProperty.call(flags, name));
    const value = key && flags[key];
    return value === 'true' || value === 'false' ? value : undefined;
  } catch {
    return undefined;
  }
};

export function getMetWorkRequirementsSplit(state = getStore().getState()) {
  const application = state.application?.application;
  const flags = state[FLOPFLIP_STATE_SLICE]?.flags;
  const memory = flags?.memory;
  // Environment off is always off; memory may independently turn it off too.
  const isExperimentEnabled = defaultFeatureFlags[featureFlags.MET_WORK_REQ_ENABLED] === true
    && (memory?.metWorkReqEnabled ?? flags?.metWorkReqEnabled ?? true) === true;
  const assignment = getMetWorkRequirementsAssignment(application);
  const applicationLoading = !application?.id
    || state.application?.applicationStatus?.getStatus === 'FETCHING';
  const hydrated = memory?.metWorkReqApplicationId === application?.id
    && memory?.metWorkReqTreatmentEnabled === (assignment === 'true');
  let status = assignment === undefined ? 'unassigned' : 'assigned';
  if (applicationLoading || (assignment !== undefined && !hydrated)) status = 'loading';
  const isAssigned = status === 'assigned';
  const isTreatment = isExperimentEnabled && isAssigned
    && memory?.metWorkReqTreatmentEnabled === true;
  return {
    applicationId: application?.id,
    status,
    isAssigned,
    isExperimentEnabled,
    isTreatment,
    variant: isTreatment ? 'treatment' : 'control',
  };
}

export const applyFeatureFlagsToNav = (navigation, featureAllFlags) => {
  const financialPlanPage = navigation.pages.find(page => page.label === 'Financial Plan');

  const interstitialPage = navigation.pages.find(page => page.label === 'Financial Plan')?.secondarySteps.find(secondaryStep => secondaryStep.label === 'Intro');
  if (interstitialPage) {
    if (featureAllFlags.ENABLE_NEW_NAVIGATION === 'true') {
      interstitialPage.enabled = true;
      interstitialPage.link = '/admissions/financial/choose-your-path';
      interstitialPage.label = 'Start Financial Plan';
    } else {
      interstitialPage.enabled = false;
    }
  }

  // Every application has the same financial-step shape; the saved assignment
  // controls availability, never whether the step occupies its canonical slot.
  const redesignEnabled = featureAllFlags.STACKABLE_SAVINGS_REDESIGN_ENABLED === 'true';
  const costSavingsStep = financialPlanPage?.secondarySteps?.find(
    secondaryStep => secondaryStep.name === 'costSavings',
  );

  if (costSavingsStep) {
    costSavingsStep.enabled = redesignEnabled && Boolean(interstitialPage?.enabled);
  } else if (financialPlanPage) {
    const introIndex = financialPlanPage.secondarySteps.findIndex(
      secondaryStep => secondaryStep.name === 'financial',
    );

    const newCostSavingsStep = {
      label: 'University Savings',
      name: 'costSavings',
      enabled: redesignEnabled && introIndex >= 0 && Boolean(interstitialPage?.enabled),
      visited: false,
      link: '/admissions/financial/cost-savings',
    };

    if (introIndex >= 0) {
      financialPlanPage.secondarySteps.splice(introIndex + 1, 0, newCostSavingsStep);
    } else {
      financialPlanPage.secondarySteps.unshift(newCostSavingsStep);
    }
  }

  return navigation;
};

// Feature Flags
// batch update multiple flags
export const updateFlopFlipFeatureFlags = (flagValues, { application, source } = {}) => {
  // Convert into right format and update in flopflip
  const flags = {};
  Object.keys(flagValues).forEach(flag => {
    if ((application || source === 'application' || source === 'preferences')
      && ['MET_WORK_REQ_TREATMENT_ENABLED', 'MET_WORK_REQ_ENABLED'].includes(flag)) return;
    const name = featureFlags[flag];
    if (name) {
      flags[name] = flagValues[flag] === 'true';
    }
  });

  if (source === 'application') {
    flags.metWorkReqTreatmentEnabled = getMetWorkRequirementsAssignment(application) === 'true';
    // Hydration provenance, not an assignment or configurable feature flag.
    flags.metWorkReqApplicationId = application?.id || '';
  }

  // Use the memory adapter's updateFlags method
  try {
    adapter.updateFlags(flags);
  } catch (error) {
    logger.error('Error updating FlopFlip flags:', error);
  }
};

// update a single flag
export const updateSingleFlag = (flagName, value) => {
  const flagUpdate = {
    [flagName]: value ? 'true' : 'false',
  };
  updateFlopFlipFeatureFlags(flagUpdate);
};
